import { ExternalLink } from 'lucide-react';
import NextLink from 'next/link';
import { useMemo } from 'react';

import { Badge, KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';

import { Trigger } from '../../domain';

interface Props {
  trigger: Trigger;
}

export function XActionTriggerFullDetails({ trigger }: Props) {
  const executedWhenDisplay = useMemo(() => {
    const isFollowTrigger = trigger.signature?.startsWith('x-follow-') ?? false;
    const isPostTrigger = trigger.signature?.startsWith('x-verify-post-') ?? false;

    if (isFollowTrigger && trigger.signature) {
      // Extract account name from signature
      const account = trigger.signature.split('x-follow-')[1];
      if (!account) return '-';

      const twitterUrl = `https://x.com/${account}`;

      return (
        <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
          <Text as="span" variant="body-sm" color="muted">
            User follows
          </Text>
          <NextLink
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:flex tw:gap-0.5 tw:no-underline"
          >
            <Badge size="sm">
              @{account}
              <ExternalLink size={12} />
            </Badge>
          </NextLink>
          <Text as="span" variant="body-sm" color="muted">
            account
          </Text>
        </div>
      );
    }

    if (isPostTrigger && trigger.signature) {
      // Extract post text from signature (e.g., "x-verify-post-Repost this tween and win a XBOX or PS5!!!")
      const postText = trigger.signature.split('x-verify-post-')[1];
      if (!postText) return '-';

      return (
        <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
          <Text as="span" variant="body-sm" color="muted">
            User posts:
          </Text>
          <Badge size="sm">{postText}</Badge>
        </div>
      );
    }

    return '-';
  }, [trigger.signature]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed when" value={executedWhenDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
